import { forwardRef, useId, useState, type InputHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  /**
   * Champ obligatoire : astérisque + `aria-required`. L'attribut natif
   * `required` n'est pas posé — la validation reste celle du formulaire
   * (messages de l'app, pas bulles du navigateur).
   */
  required?: boolean;
  /** Icône à gauche dans le champ (loupe, lieu…), décorative. */
  leftIcon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, helperText, id, type, required, leftIcon, ...props }, ref) => {
    const { t } = useTranslation();
    // useId garantit un id unique même quand deux champs partagent un label.
    const autoId = useId();
    const inputId = id ?? autoId;
    const errorId = `${inputId}-error`;
    const helpId = `${inputId}-help`;

    // Tout champ mot de passe est révélable : évite les fautes de frappe
    // invisibles, sans que chaque page ait à réimplémenter le bouton œil.
    const isPassword = type === 'password';
    const [revealed, setRevealed] = useState(false);

    return (
      <div className="space-y-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-[13px] font-medium text-text"
          >
            {label}
            {required && <span aria-hidden="true" className="ml-0.5 text-danger">*</span>}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <span className="pointer-events-none absolute inset-y-0 left-3 z-10 flex items-center text-subtle [&>svg]:h-4 [&>svg]:w-4" aria-hidden="true">
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            type={isPassword && revealed ? 'text' : type}
            aria-invalid={error ? true : undefined}
            aria-required={required || undefined}
            aria-describedby={error ? errorId : helperText ? helpId : undefined}
            className={cn(
              'block w-full rounded border border-border bg-surface px-3.5 py-2.5 text-sm text-text transition-all duration-150',
              'placeholder:text-subtle',
              'focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent',
              error && 'border-danger focus:border-danger focus:ring-danger',
              'disabled:cursor-not-allowed disabled:bg-surface-subtle disabled:text-subtle',
              isPassword && 'pr-10',
              leftIcon && 'pl-9',
              className,
            )}
            {...props}
          />
          {isPassword && (
            <button
              type="button"
              onClick={() => setRevealed((v) => !v)}
              aria-label={revealed ? t('common.hidePassword') : t('common.showPassword')}
              className="absolute inset-y-0 right-0 flex items-center rounded px-3 text-subtle transition-colors hover:text-text focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
            >
              {revealed ? (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Eye className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          )}
        </div>
        {error && <p id={errorId} className="text-xs text-danger">{error}</p>}
        {helperText && !error && (
          <p id={helpId} className="text-xs text-subtle">{helperText}</p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
